import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Loader2, Languages, Database, Flame, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RESOURCES = [
  { value: 'matthew-henry', label: 'Matthew Henry' },
  { value: 'jfb', label: 'Jamieson-Fausset-Brown' },
  { value: 'clarke', label: 'Adam Clarke' },
];

const BOOKS = [
  'Genesis', 'Exodus', 'Psalms', 'Proverbs', 'Isaiah', 'Matthew', 'Mark',
  'Luke', 'John', 'Acts', 'Romans', '1 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Hebrews', 'James', '1 Peter', '1 John', 'Revelation', 'Jude',
];

/**
 * TranslationCacheAdminPanel — SuperAdmin view of the `translation_cache`:
 * total entries + by-language breakdown + recent sources, plus a "pre-translate
 * a whole book" action that warms the cache in the background (progress polled).
 */
const TranslationCacheAdminPanel = ({ language = 'en' }) => {
  const es = language === 'es';
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resource, setResource] = useState('matthew-henry');
  const [book, setBook] = useState('John');
  const [starting, setStarting] = useState(false);

  const loadStats = async () => {
    try {
      const [s, j] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/translate/admin/stats`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/translate/admin/jobs`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setJobs(j.data.jobs || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const id = setInterval(loadStats, 5000); // poll for job progress + fresh stats
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPrewarm = async () => {
    setStarting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/translate/admin/prewarm`,
        { resource, book, target: 'es' },
        { withCredentials: true },
      );
      toast.success(es
        ? `Pre-traduciendo ${book} (${r.data.total_chapters} capítulos) en segundo plano…`
        : `Pre-translating ${book} (${r.data.total_chapters} chapters) in the background…`);
      loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (es ? 'No se pudo iniciar' : 'Could not start'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card className="border-2 border-indigo-300/50 dark:border-indigo-700/40" data-testid="translation-admin-panel">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
            <Languages className="w-5 h-5" />
            {es ? 'Caché de traducción' : 'Translation Cache'}
          </span>
          <Button variant="ghost" size="sm" onClick={loadStats} data-testid="translation-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        {loading && !stats ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> {es ? 'Cargando…' : 'Loading…'}
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="flex flex-wrap items-center gap-3" data-testid="translation-stats">
              <div className="rounded-lg bg-muted px-4 py-3 text-center min-w-[130px]">
                <p className="text-2xl font-bold flex items-center justify-center gap-1.5">
                  <Database className="w-4 h-4 text-indigo-500" />{stats?.total ?? 0}
                </p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">
                  {es ? 'Entradas' : 'Cached entries'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(stats?.by_target || {}).map(([lang, n]) => (
                  <Badge key={lang} variant="secondary" className="uppercase">{lang}: {n}</Badge>
                ))}
              </div>
            </div>

            {/* Pre-warm */}
            <div className="rounded-lg border border-amber-300/50 dark:border-amber-700/40 bg-amber-50/50 dark:bg-amber-900/10 p-4 space-y-3">
              <p className="flex items-center gap-2 font-semibold text-amber-800 dark:text-amber-300">
                <Flame className="w-4 h-4" /> {es ? 'Pre-traducir un libro' : 'Pre-translate a book'}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {es
                  ? 'Traduce y almacena en caché un libro completo para que los usuarios en español nunca esperen en la primera carga.'
                  : 'Translate & cache a whole book so Spanish users never wait on first load.'}
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-56">
                  <Select value={resource} onValueChange={setResource}>
                    <SelectTrigger data-testid="prewarm-resource"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RESOURCES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-44">
                  <Select value={book} onValueChange={setBook}>
                    <SelectTrigger data-testid="prewarm-book"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {BOOKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={startPrewarm}
                  disabled={starting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="prewarm-start-btn"
                >
                  {starting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Flame className="w-4 h-4 mr-1" />}
                  {es ? 'Pre-traducir' : 'Pre-translate'}
                </Button>
              </div>
            </div>

            {/* Jobs */}
            {jobs.length > 0 && (
              <div className="space-y-2" data-testid="translation-jobs">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">
                  {es ? 'Trabajos recientes' : 'Recent jobs'}
                </p>
                {jobs.map((j) => {
                  const pct = j.total_chapters ? Math.round((j.done / j.total_chapters) * 100) : 0;
                  return (
                    <div key={j.id} className="space-y-1" data-testid={`translation-job-${j.id}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">
                          {RESOURCES.find((r) => r.value === j.resource)?.label || j.resource} · {j.book} · {j.target.toUpperCase()}
                        </span>
                        <span className={j.status === 'done' ? 'text-emerald-600' : 'text-amber-600'}>
                          {j.done}/{j.total_chapters} · {j.status === 'done' ? (es ? 'listo' : 'done') : (es ? 'en curso' : 'running')}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${j.status === 'done' ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TranslationCacheAdminPanel;
